import React, { useState, useEffect } from 'react';
import { useConfirm, ConfirmDialog } from '@/lib/confirm';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { PlusCircle, Edit, Trash2, Calendar, Image, Eye, MousePointer, Upload, X as XIcon, Pencil, Star, Award } from 'lucide-react';
import { toast } from 'sonner';
import RichTextEditor from '@/components/RichTextEditor';
import RichText from '@/components/RichText';

const BACKEND_URL = '';

export function AdminSponsorsTab() {
  const [sponsors, setSponsors] = useState([]);
  const [courses, setCourses] = useState([]);
  const [showSponsorDialog, setShowSponsorDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [sponsorForm, setSponsorForm] = useState({
    name: '', description: '', logo_url: '', website: '', categories: '', contact_email: ''
  });
  const [sponsorBanners, setSponsorBanners] = useState({});
  const [showBannerDialog, setShowBannerDialog] = useState(null);
  const [bannerForm, setBannerForm] = useState({ banner_type: 'top', placement: 'app', click_url: '', file: null, campaign_start: '', campaign_end: '' });
  const [editingBanner, setEditingBanner] = useState(null);
  const [editBannerForm, setEditBannerForm] = useState({});
  const [memberSearch, setMemberSearch] = useState('');
  const [allMembers, setAllMembers] = useState([]);
  const { confirm, dialogProps } = useConfirm();

  useEffect(() => { loadSponsors(); loadCourses(); loadMembers(); }, []);

  const loadSponsors = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/sponsors?include_hidden=true`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        const list = Array.isArray(data) ? data : [];
        setSponsors(list);
        list.forEach(s => loadSponsorBanners(s.sponsor_id));
      }
    } catch {}
  };

  const loadCourses = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/courses`, { credentials: 'include' });
      if (res.ok) setCourses(await res.json());
    } catch {}
  };

  const loadMembers = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/members`, { credentials: 'include' });
      if (res.ok) setAllMembers(await res.json());
    } catch {}
  };

  const uploadAsset = async (sponsorId, assetType, file) => {
    if (!sponsorId) {
      toast.error('Save partner first, then upload assets');
      return null;
    }
    const fd = new FormData();
    fd.append('asset_type', assetType);
    fd.append('file', file);
    const res = await fetch(`${BACKEND_URL}/api/partners/${sponsorId}/upload-asset`, { method: 'POST', credentials: 'include', body: fd });
    if (!res.ok) { toast.error('Upload failed'); return null; }
    return await res.json();
  };

  const loadSponsorBanners = async (sponsorId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/sponsors/${sponsorId}/banners`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setSponsorBanners(prev => ({ ...prev, [sponsorId]: Array.isArray(data) ? data : [] }));
      }
    } catch {}
  };

  const handleCreateSponsor = async () => {
    try {
      const payload = {
        ...sponsorForm,
        categories: sponsorForm.categories.split(',').map(c => c.trim()).filter(Boolean),
        about_sections: (sponsorForm.about_sections || []).filter(a => a.title || a.description || a.media_url),
        latest_news: (sponsorForm.latest_news || []).filter(n => n.title || n.overview || n.article_url),
        manual_team_user_ids: sponsorForm.manual_team_user_ids || [],
      };
      const method = editingItem ? 'PUT' : 'POST';
      const url = editingItem ? `${BACKEND_URL}/api/sponsors/${editingItem.sponsor_id}` : `${BACKEND_URL}/api/sponsors`;
      const res = await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(payload) });
      if (!res.ok) throw new Error('Failed to save sponsor');
      toast.success(editingItem ? 'Sponsor updated!' : 'Sponsor created!');
      setShowSponsorDialog(false);
      setEditingItem(null);
      setSponsorForm({ name: '', description: '', logo_url: '', logo_description: '', website: '', categories: '', contact_email: '', about_sections: [], latest_news: [], manual_team_user_ids: [] });
      loadSponsors();
    } catch { toast.error('Failed to save sponsor'); }
  };

  const handleDeleteSponsor = async (sponsorId) => {
    const ok = await confirm(
      'Delete this sponsor?',
      'Banners attached to it will also be removed. This cannot be undone.',
      { confirmText: 'Delete', destructive: true }
    );
    if (!ok) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/sponsors/${sponsorId}`, { method: 'DELETE', credentials: 'include' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      toast.success('Deleted successfully');
      loadSponsors();
    } catch (e) { toast.error(`Could not delete: ${e.message}`); }
  };

  const startEditSponsor = (sponsor) => {
    setEditingItem(sponsor);
    setSponsorForm({
      name: sponsor.name, description: sponsor.description,
      logo_url: sponsor.logo_url || '', logo_description: sponsor.logo_description || '',
      website: sponsor.website || '',
      categories: sponsor.categories?.join(', ') || '', contact_email: sponsor.contact_email || '',
      about_sections: sponsor.about_sections || [],
      latest_news: sponsor.latest_news || [],
      manual_team_user_ids: sponsor.manual_team_user_ids || [],
    });
    setShowSponsorDialog(true);
  };

  const handleCreateBanner = async (sponsorId) => {
    if (!bannerForm.file) { toast.error('Please select a banner image'); return; }
    try {
      const formData = new FormData();
      formData.append('file', bannerForm.file);
      formData.append('banner_type', bannerForm.banner_type);
      formData.append('placement', bannerForm.placement);
      formData.append('click_url', bannerForm.click_url);
      formData.append('enabled', 'true');
      if (bannerForm.campaign_start) formData.append('campaign_start', bannerForm.campaign_start);
      if (bannerForm.campaign_end) formData.append('campaign_end', bannerForm.campaign_end);
      const res = await fetch(`${BACKEND_URL}/api/sponsors/${sponsorId}/banners`, { method: 'POST', credentials: 'include', body: formData });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Failed'); }
      toast.success('Banner created!');
      setShowBannerDialog(null);
      setBannerForm({ banner_type: 'top', placement: 'app', click_url: '', file: null, campaign_start: '', campaign_end: '' });
      loadSponsorBanners(sponsorId);
      loadSponsors();
    } catch (e) { toast.error(e.message); }
  };

  const handleDeleteBanner = async (bannerId, sponsorId) => {
    const ok = await confirm(
      'Delete this banner?',
      'This cannot be undone.',
      { confirmText: 'Delete', destructive: true }
    );
    if (!ok) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/sponsors/banners/${bannerId}`, { method: 'DELETE', credentials: 'include' });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || `Delete failed (${res.status})`); }
      setSponsorBanners(prev => ({ ...prev, [sponsorId]: (prev[sponsorId] || []).filter(b => b.banner_id !== bannerId) }));
      toast.success('Banner deleted');
      loadSponsors();
    } catch (e) { toast.error(e.message); }
  };

  const handleToggleBanner = async (bannerId, enabled, sponsorId) => {
    try {
      const formData = new FormData();
      formData.append('enabled', enabled ? 'true' : 'false');
      await fetch(`${BACKEND_URL}/api/sponsors/banners/${bannerId}`, { method: 'PUT', credentials: 'include', body: formData });
      loadSponsorBanners(sponsorId);
    } catch {}
  };

  const handleEditBanner = (banner) => {
    setEditingBanner(banner.banner_id);
    setEditBannerForm({
      click_url: banner.click_url || '', placement: banner.placement || 'app',
      banner_type: banner.banner_type || 'top', campaign_start: banner.campaign_start || '', campaign_end: banner.campaign_end || '',
      enabled: banner.enabled !== false,
      file: null,
      // Keep the existing image preview visible until the admin actively
      // picks a replacement file. Avoids surprising blank-state UX.
      current_image_url: banner.image_url,
    });
  };

  const handleSaveBannerEdit = async (bannerId, sponsorId) => {
    try {
      const formData = new FormData();
      formData.append('click_url', editBannerForm.click_url);
      formData.append('placement', editBannerForm.placement);
      formData.append('banner_type', editBannerForm.banner_type);
      formData.append('campaign_start', editBannerForm.campaign_start);
      formData.append('campaign_end', editBannerForm.campaign_end);
      formData.append('enabled', editBannerForm.enabled ? 'true' : 'false');
      if (editBannerForm.file) formData.append('file', editBannerForm.file);
      const res = await fetch(`${BACKEND_URL}/api/sponsors/banners/${bannerId}`, { method: 'PUT', credentials: 'include', body: formData });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || 'Update failed'); }
      toast.success('Banner updated');
      setEditingBanner(null);
      loadSponsorBanners(sponsorId);
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-4">
      <Dialog open={showSponsorDialog} onOpenChange={setShowSponsorDialog}>
        <DialogTrigger asChild>
          <Button data-testid="create-sponsor-button" onClick={() => { setEditingItem(null); setSponsorForm({ name: '', description: '', logo_url: '', logo_description: '', website: '', categories: '', contact_email: '', about_sections: [], latest_news: [], manual_team_user_ids: [] }); }} className="bg-gradient-to-r from-brand-navy to-brand-sage text-white">
            <PlusCircle className="h-4 w-4 mr-2" /> Create Sponsor
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingItem ? 'Edit Sponsor' : 'Create New Sponsor'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Name</Label><Input value={sponsorForm.name} onChange={(e) => setSponsorForm({...sponsorForm, name: e.target.value})} /></div>
            <div><Label>Description</Label><RichTextEditor value={sponsorForm.description} onChange={v => setSponsorForm({...sponsorForm, description: v})} testId="sponsor-description-input" minHeight="7rem" placeholder="Description shown on the partner profile page" /></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-start">
              <div>
                <Label>Logo</Label>
                {sponsorForm.logo_url && (
                  <div className="mt-1 flex items-center gap-2 p-2 border border-slate-200 rounded">
                    <img src={sponsorForm.logo_url} alt="logo preview" className="h-10 w-10 object-contain" />
                    <button type="button" onClick={() => setSponsorForm({...sponsorForm, logo_url: ''})} className="text-xs text-red-500 hover:underline">Remove</button>
                  </div>
                )}
                <Input value={sponsorForm.logo_url} placeholder="Paste URL or upload below"
                  onChange={(e) => setSponsorForm({...sponsorForm, logo_url: e.target.value})} className="mt-1" />
                {editingItem && (
                  <input type="file" accept="image/*" data-testid="logo-upload"
                    className="mt-2 text-xs"
                    onChange={async (e) => {
                      const f = e.target.files?.[0]; if (!f) return;
                      const r = await uploadAsset(editingItem.sponsor_id, 'logo', f);
                      if (r) setSponsorForm(p => ({...p, logo_url: r.url}));
                    }} />
                )}
              </div>
              <div>
                <Label>Logo Description</Label>
                <RichTextEditor value={sponsorForm.logo_description} placeholder="Short caption shown next to the logo"
                  onChange={v => setSponsorForm({...sponsorForm, logo_description: v})} testId="sponsor-logo-desc" minHeight="5rem" />
              </div>
            </div>
            <div><Label>Website</Label><Input value={sponsorForm.website} onChange={(e) => setSponsorForm({...sponsorForm, website: e.target.value})} /></div>
            <div><Label>Contact Email</Label><Input type="email" value={sponsorForm.contact_email} onChange={(e) => setSponsorForm({...sponsorForm, contact_email: e.target.value})} /></div>
            <div><Label>Categories (comma-separated)</Label><Input value={sponsorForm.categories} onChange={(e) => setSponsorForm({...sponsorForm, categories: e.target.value})} /></div>

            {/* Manual Team Linking */}
            {editingItem && (
              <div className="border-t pt-4" data-testid="manual-team-section">
                <Label className="text-base">Manually Link Team Members</Label>
                <p className="text-xs text-slate-500 mb-2">Members whose profile organisation matches "{sponsorForm.name}" are auto-linked. Add others here.</p>
                <Input placeholder="Search members by name..." value={memberSearch} onChange={e => setMemberSearch(e.target.value)} className="mb-2" />
                <div className="max-h-48 overflow-y-auto border border-slate-200 rounded">
                  {allMembers.filter(m => !memberSearch || m.name?.toLowerCase().includes(memberSearch.toLowerCase())).slice(0, 50).map(m => {
                    const checked = (sponsorForm.manual_team_user_ids || []).includes(m.user_id);
                    return (
                      <label key={m.user_id} className="flex items-center gap-2 p-2 hover:bg-slate-50 cursor-pointer text-sm">
                        <input type="checkbox" checked={checked} onChange={e => {
                          const ids = sponsorForm.manual_team_user_ids || [];
                          setSponsorForm({...sponsorForm, manual_team_user_ids: e.target.checked ? [...ids, m.user_id] : ids.filter(i => i !== m.user_id)});
                        }} />
                        <span className="font-medium">{m.name}</span>
                        <span className="text-slate-400 text-xs">{m.profile?.organisation || 'No org'}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            {/* About Us Sections (up to 5) */}
            <div className="border-t pt-4" data-testid="about-sections">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-base">Enabling Change — up to 5 cards</Label>
                {(sponsorForm.about_sections || []).length < 5 && (
                  <Button type="button" size="sm" variant="outline" onClick={() => setSponsorForm(p => ({...p, about_sections: [...(p.about_sections || []), {title: '', description: '', media_url: '', media_type: 'pdf'}]}))} data-testid="add-about-section">
                    <PlusCircle className="h-3 w-3 mr-1" /> Add card
                  </Button>
                )}
              </div>
              {(sponsorForm.about_sections || []).map((sec, idx) => (
                <div key={idx} className="p-3 border border-slate-200 rounded mb-2 space-y-2 bg-slate-50" data-testid={`about-section-${idx}`}>
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-semibold text-slate-500">Card {idx + 1}</p>
                    <button type="button" className="text-red-500 text-xs" onClick={() => setSponsorForm(p => ({...p, about_sections: p.about_sections.filter((_, i) => i !== idx)}))}><XIcon className="h-3 w-3" /></button>
                  </div>
                  <Input placeholder="Title" value={sec.title} onChange={e => { const arr = [...sponsorForm.about_sections]; arr[idx].title = e.target.value; setSponsorForm({...sponsorForm, about_sections: arr}); }} />
                  <RichTextEditor placeholder="Description" value={sec.description} onChange={v => { const arr = [...sponsorForm.about_sections]; arr[idx].description = v; setSponsorForm({...sponsorForm, about_sections: arr}); }} testId={`sponsor-about-desc-${idx}`} minHeight="5rem" />
                  <div className="flex gap-2 items-center">
                    <Select value={sec.media_type || 'pdf'} onValueChange={v => { const arr = [...sponsorForm.about_sections]; arr[idx].media_type = v; setSponsorForm({...sponsorForm, about_sections: arr}); }}>
                      <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pdf">PDF</SelectItem>
                        <SelectItem value="vimeo">Vimeo</SelectItem>
                      </SelectContent>
                    </Select>
                    {sec.media_type === 'vimeo' ? (
                      <Input placeholder="https://vimeo.com/..." value={sec.media_url} onChange={e => { const arr = [...sponsorForm.about_sections]; arr[idx].media_url = e.target.value; setSponsorForm({...sponsorForm, about_sections: arr}); }} />
                    ) : (
                      <>
                        <Input placeholder="PDF URL" value={sec.media_url} onChange={e => { const arr = [...sponsorForm.about_sections]; arr[idx].media_url = e.target.value; setSponsorForm({...sponsorForm, about_sections: arr}); }} />
                        {editingItem && (
                          <input type="file" accept="application/pdf" className="text-xs"
                            onChange={async e => {
                              const f = e.target.files?.[0]; if (!f) return;
                              const r = await uploadAsset(editingItem.sponsor_id, 'about_pdf', f);
                              if (r) {
                                const arr = [...sponsorForm.about_sections];
                                arr[idx].media_url = r.url;
                                arr[idx].media_filename = r.filename;
                                setSponsorForm({...sponsorForm, about_sections: arr});
                              }
                            }} />
                        )}
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Latest News (up to 3) */}
            <div className="border-t pt-4" data-testid="latest-news-sections">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-base">Latest News — up to 3 articles</Label>
                {(sponsorForm.latest_news || []).length < 3 && (
                  <Button type="button" size="sm" variant="outline" onClick={() => setSponsorForm(p => ({...p, latest_news: [...(p.latest_news || []), {title: '', overview: '', image_url: '', article_url: ''}]}))} data-testid="add-news-article">
                    <PlusCircle className="h-3 w-3 mr-1" /> Add article
                  </Button>
                )}
              </div>
              {(sponsorForm.latest_news || []).map((art, idx) => (
                <div key={idx} className="p-3 border border-slate-200 rounded mb-2 space-y-2 bg-slate-50" data-testid={`news-article-${idx}`}>
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-semibold text-slate-500">Article {idx + 1}</p>
                    <button type="button" className="text-red-500 text-xs" onClick={() => setSponsorForm(p => ({...p, latest_news: p.latest_news.filter((_, i) => i !== idx)}))}><XIcon className="h-3 w-3" /></button>
                  </div>
                  <Input placeholder="Headline" value={art.title} onChange={e => { const arr = [...sponsorForm.latest_news]; arr[idx].title = e.target.value; setSponsorForm({...sponsorForm, latest_news: arr}); }} />
                  <RichTextEditor placeholder="Overview" value={art.overview} onChange={v => { const arr = [...sponsorForm.latest_news]; arr[idx].overview = v; setSponsorForm({...sponsorForm, latest_news: arr}); }} testId={`sponsor-news-overview-${idx}`} minHeight="5rem" />
                  <Input placeholder="Article URL (internal /news/:id or external)" value={art.article_url} onChange={e => { const arr = [...sponsorForm.latest_news]; arr[idx].article_url = e.target.value; setSponsorForm({...sponsorForm, latest_news: arr}); }} />
                  <div className="flex items-center gap-2">
                    <Input placeholder="Image URL" value={art.image_url} onChange={e => { const arr = [...sponsorForm.latest_news]; arr[idx].image_url = e.target.value; setSponsorForm({...sponsorForm, latest_news: arr}); }} />
                    {editingItem && (
                      <input type="file" accept="image/*" className="text-xs"
                        onChange={async e => {
                          const f = e.target.files?.[0]; if (!f) return;
                          const r = await uploadAsset(editingItem.sponsor_id, 'news_image', f);
                          if (r) { const arr = [...sponsorForm.latest_news]; arr[idx].image_url = r.url; setSponsorForm({...sponsorForm, latest_news: arr}); }
                        }} />
                    )}
                  </div>
                  {art.image_url && <img src={art.image_url} alt="" className="h-16 rounded object-cover" />}
                </div>
              ))}
            </div>

            <Button data-testid="save-sponsor-button" onClick={handleCreateSponsor} className="w-full bg-gradient-to-r from-brand-navy to-brand-sage text-white">
              {editingItem ? 'Update Sponsor' : 'Create Sponsor'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <div className="grid gap-4">
        {sponsors.map(sponsor => (
          <Card key={sponsor.sponsor_id} data-testid={`admin-sponsor-${sponsor.sponsor_id}`} className={`p-6 ${sponsor.hidden ? 'border-amber-300 bg-amber-50/40' : ''}`}>
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <h3 className="text-xl font-bold font-heading">{sponsor.name}</h3>
                  {sponsor.hidden && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200 uppercase tracking-wider font-bold" data-testid={`sponsor-hidden-badge-${sponsor.sponsor_id}`}>
                      Hidden
                    </span>
                  )}
                  {sponsor.sponsor_id?.startsWith('partner_from_') && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200 uppercase tracking-wider">
                      Synced from company
                    </span>
                  )}
                </div>
                <div className="text-slate-600 mb-2"><RichText html={sponsor.description} className="card card-2" /></div>
                {sponsor.website && <p className="text-sm text-slate-500">{sponsor.website}</p>}
                {sponsor.hidden && (
                  <p className="text-xs text-amber-700 mt-2">
                    This partner is currently hidden from the public directory. Banners and analytics are preserved.
                  </p>
                )}
              </div>
              <div className="flex space-x-2">
                {sponsor.hidden && (
                  <Button
                    onClick={async () => {
                      const r = await fetch(`${BACKEND_URL}/api/sponsors/${sponsor.sponsor_id}/restore`, {
                        method: 'PUT', credentials: 'include',
                      });
                      if (r.ok) {
                        toast.success('Partner restored');
                        loadSponsors();
                      } else {
                        toast.error('Restore failed');
                      }
                    }}
                    variant="outline"
                    size="sm"
                    className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                    data-testid={`restore-sponsor-${sponsor.sponsor_id}`}
                  >
                    Restore
                  </Button>
                )}
                <Button data-testid={`edit-sponsor-${sponsor.sponsor_id}`} onClick={() => startEditSponsor(sponsor)} variant="outline" size="sm"><Edit className="h-4 w-4" /></Button>
                <Button data-testid={`delete-sponsor-${sponsor.sponsor_id}`} onClick={() => handleDeleteSponsor(sponsor.sponsor_id)} variant="outline" size="sm" className="text-red-600"><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>

            {/* Recommendation Management */}
            <div className="mt-4 pt-4 border-t">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-bold text-slate-700 flex items-center gap-2"><Award className="h-4 w-4" /> Recommendation</h4>
                {sponsor.recommended ? (
                  <span className="text-xs font-semibold text-amber-700 bg-amber-50 px-2.5 py-1 rounded-full">Recommended (Priority: {sponsor.recommended_priority || '-'})</span>
                ) : (
                  <span className="text-xs text-slate-400">Not recommended</span>
                )}
              </div>
              <div className="grid grid-cols-4 gap-2">
                <div>
                  <Label className="text-[10px] text-slate-400">Topic</Label>
                  <Select value={sponsor.recommended_topic || ''} onValueChange={async (v) => {
                    await fetch(`${BACKEND_URL}/api/sponsors/${sponsor.sponsor_id}/recommend`, {
                      method: 'PUT', credentials: 'include',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ recommended: true, recommended_topic: v, recommended_priority: sponsor.recommended_priority || 1, recommended_reason: sponsor.recommended_reason || '' }),
                    });
                    loadSponsors();
                  }}>
                    <SelectTrigger className="h-8 text-xs" data-testid={`rec-topic-${sponsor.sponsor_id}`}><SelectValue placeholder="Select topic" /></SelectTrigger>
                    <SelectContent>
                      {["AI Adoption","Air","Carbon Accounting","Certification","Change Management","Commercialisation","Electrification","Freight","Ocean","Rail","Road","Warehousing/ Fulfilment","General"].map(t => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] text-slate-400">Priority (1=highest)</Label>
                  <Input type="number" min={1} max={99} className="h-8 text-xs" value={sponsor.recommended_priority || ''}
                    data-testid={`rec-priority-${sponsor.sponsor_id}`}
                    onChange={async (e) => {
                      const val = parseInt(e.target.value) || 10;
                      await fetch(`${BACKEND_URL}/api/sponsors/${sponsor.sponsor_id}/recommend`, {
                        method: 'PUT', credentials: 'include',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ recommended: true, recommended_topic: sponsor.recommended_topic || '', recommended_priority: val, recommended_reason: sponsor.recommended_reason || '' }),
                      });
                      loadSponsors();
                    }} />
                </div>
                <div>
                  <Label className="text-[10px] text-slate-400">Reason</Label>
                  <Input className="h-8 text-xs" placeholder="Why recommended?" value={sponsor.recommended_reason || ''}
                    data-testid={`rec-reason-${sponsor.sponsor_id}`}
                    onBlur={async (e) => {
                      await fetch(`${BACKEND_URL}/api/sponsors/${sponsor.sponsor_id}/recommend`, {
                        method: 'PUT', credentials: 'include',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ recommended: true, recommended_topic: sponsor.recommended_topic || '', recommended_priority: sponsor.recommended_priority || 10, recommended_reason: e.target.value }),
                      });
                      loadSponsors();
                    }} />
                </div>
                <div className="flex items-end">
                  <Button variant={sponsor.recommended ? "default" : "outline"} size="sm" className={`h-8 text-xs w-full ${sponsor.recommended ? 'bg-amber-500 hover:bg-amber-600 text-white' : ''}`}
                    data-testid={`toggle-rec-${sponsor.sponsor_id}`}
                    onClick={async () => {
                      await fetch(`${BACKEND_URL}/api/sponsors/${sponsor.sponsor_id}/recommend`, {
                        method: 'PUT', credentials: 'include',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ recommended: !sponsor.recommended, recommended_topic: sponsor.recommended_topic || '', recommended_priority: sponsor.recommended_priority || 10, recommended_reason: sponsor.recommended_reason || '' }),
                      });
                      toast.success(sponsor.recommended ? 'Recommendation removed' : 'Partner recommended');
                      loadSponsors();
                    }}>
                    <Star className={`h-3.5 w-3.5 mr-1 ${sponsor.recommended ? 'fill-white' : ''}`} />
                    {sponsor.recommended ? 'Recommended' : 'Recommend'}
                  </Button>
                </div>
              </div>
            </div>

            {/* Banner Management */}
            <div className="mt-4 pt-4 border-t">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-bold text-slate-700 flex items-center gap-2"><Image className="h-4 w-4" /> Banner Ads</h4>
                <Button variant="outline" size="sm" data-testid={`add-banner-${sponsor.sponsor_id}`}
                  onClick={() => { setShowBannerDialog(sponsor.sponsor_id); setBannerForm({ banner_type: 'top', placement: 'app', click_url: sponsor.website || '', file: null, campaign_start: '', campaign_end: '' }); }}>
                  <Upload className="h-3.5 w-3.5 mr-1" /> Add Banner
                </Button>
              </div>

              <div className="space-y-2">
                {(!sponsorBanners[sponsor.sponsor_id] || sponsorBanners[sponsor.sponsor_id].length === 0) ? (
                  <p className="text-xs text-slate-400">No banners configured</p>
                ) : (
                  sponsorBanners[sponsor.sponsor_id].map(banner => (
                    <div key={banner.banner_id} data-testid={`banner-${banner.banner_id}`} className="p-3 rounded-lg bg-slate-50 border">
                      {editingBanner === banner.banner_id ? (
                        <div className="space-y-3" data-testid={`edit-banner-form-${banner.banner_id}`}>
                          <div className="flex items-center justify-between">
                            <h5 className="text-xs font-bold text-slate-700">Edit Banner</h5>
                            <button onClick={() => setEditingBanner(null)} className="text-slate-400 hover:text-slate-600"><XIcon className="h-4 w-4" /></button>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <Label className="text-xs">Type</Label>
                              <Select value={editBannerForm.banner_type} onValueChange={v => setEditBannerForm(f => ({ ...f, banner_type: v }))}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="top">Top Banner (1456x135)</SelectItem>
                                  <SelectItem value="side">Side Banner (600x375)</SelectItem>
                                  <SelectItem value="feed">In-Feed Banner (728x180)</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-xs">Placement</Label>
                              <Select value={editBannerForm.placement} onValueChange={v => setEditBannerForm(f => ({ ...f, placement: v }))}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="app">Site-wide (App)</SelectItem>
                                  <SelectItem value="forum">Forum</SelectItem>
                                  {courses.map(c => <SelectItem key={c.course_id} value={`course:${c.course_id}`}>Course: {c.title}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <div>
                            <Label className="text-xs">Click URL</Label>
                            <Input value={editBannerForm.click_url} onChange={e => setEditBannerForm(f => ({ ...f, click_url: e.target.value }))} placeholder="https://sponsor-website.com" />
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div><Label className="text-xs">Campaign Start</Label><Input type="date" value={editBannerForm.campaign_start} onChange={e => setEditBannerForm(f => ({ ...f, campaign_start: e.target.value }))} data-testid={`edit-banner-start-${banner.banner_id}`} /></div>
                            <div><Label className="text-xs">Campaign End</Label><Input type="date" value={editBannerForm.campaign_end} onChange={e => setEditBannerForm(f => ({ ...f, campaign_end: e.target.value }))} data-testid={`edit-banner-end-${banner.banner_id}`} /></div>
                          </div>
                          <div>
                            <Label className="text-xs">Banner Image (optional)</Label>
                            <div className="flex items-center gap-3 mt-1">
                              {/* Existing image stays visible so admins can
                                  see what they're replacing before they pick
                                  a new file. */}
                              <img
                                src={editBannerForm.file
                                  ? URL.createObjectURL(editBannerForm.file)
                                  : editBannerForm.current_image_url}
                                alt="Current banner"
                                className="h-12 w-20 object-contain rounded border bg-white flex-shrink-0"
                              />
                              <div className="flex-1 min-w-0">
                                <Input
                                  type="file"
                                  accept="image/*"
                                  data-testid={`edit-banner-file-${banner.banner_id}`}
                                  onChange={e => setEditBannerForm(f => ({ ...f, file: e.target.files?.[0] || null }))}
                                  className="text-xs"
                                />
                                <p className="text-[10px] text-slate-400 mt-1">
                                  Leave empty to keep the existing image. Max 5MB · recommended {editBannerForm.banner_type === 'top' ? '1456×135' : editBannerForm.banner_type === 'feed' ? '728×180' : '600×375'}.
                                </p>
                              </div>
                            </div>
                          </div>
                          <label className="flex items-center gap-2 text-xs font-medium text-slate-700 cursor-pointer select-none">
                            <input
                              type="checkbox"
                              checked={!!editBannerForm.enabled}
                              onChange={e => setEditBannerForm(f => ({ ...f, enabled: e.target.checked }))}
                              data-testid={`edit-banner-enabled-${banner.banner_id}`}
                              className="h-3.5 w-3.5"
                            />
                            Banner is live (uncheck to pause)
                          </label>
                          <div className="flex gap-2">
                            <Button data-testid={`save-edit-banner-${banner.banner_id}`} size="sm" onClick={() => handleSaveBannerEdit(banner.banner_id, sponsor.sponsor_id)} className="bg-gradient-to-r from-brand-navy to-brand-sage text-white">Save Changes</Button>
                            <Button variant="outline" size="sm" onClick={() => setEditingBanner(null)}>Cancel</Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-3">
                          <img src={banner.image_url} alt="Banner" className="h-12 w-20 object-contain rounded border bg-white" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-xs font-medium px-2 py-0.5 bg-brand-navy/10 text-brand-navy rounded">{banner.banner_type === 'top' ? 'Top 1456x135' : banner.banner_type === 'feed' ? 'In-Feed 728x180' : 'Side 600x375'}</span>
                              <span className="text-xs text-slate-500">{banner.placement}</span>
                              <span className={`text-xs px-1.5 py-0.5 rounded ${banner.enabled ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>{banner.enabled ? 'Active' : 'Paused'}</span>
                            </div>
                            <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                              <span className="flex items-center gap-1"><Eye className="h-3 w-3" /> {banner.impressions}</span>
                              <span className="flex items-center gap-1"><MousePointer className="h-3 w-3" /> {banner.clicks}</span>
                              <span>CTR: {banner.impressions > 0 ? ((banner.clicks / banner.impressions) * 100).toFixed(1) : 0}%</span>
                            </div>
                            {(banner.campaign_start || banner.campaign_end) && (
                              <div className="flex items-center gap-2 mt-1 text-xs">
                                <Calendar className="h-3 w-3 text-slate-400" />
                                <span className="text-slate-500">{banner.campaign_start || '—'} to {banner.campaign_end || '—'}</span>
                                {(() => {
                                  const today = new Date().toISOString().slice(0, 10);
                                  if (banner.campaign_start && banner.campaign_start > today) return <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">Upcoming</span>;
                                  if (banner.campaign_end && banner.campaign_end < today) return <span className="px-1.5 py-0.5 bg-slate-200 text-slate-600 rounded">Expired</span>;
                                  return <span className="px-1.5 py-0.5 bg-green-100 text-green-700 rounded">Live</span>;
                                })()}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            <Button variant="outline" size="sm" data-testid={`edit-banner-${banner.banner_id}`} onClick={() => handleEditBanner(banner)} className="text-brand-navy border-brand-navy/30 hover:bg-brand-navy/5 h-7 px-2 gap-1">
                              <Pencil className="h-3.5 w-3.5" /> Edit
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleToggleBanner(banner.banner_id, !banner.enabled, sponsor.sponsor_id)} className={banner.enabled ? 'text-amber-600' : 'text-green-600'}>{banner.enabled ? 'Pause' : 'Enable'}</Button>
                            <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDeleteBanner(banner.banner_id, sponsor.sponsor_id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>

              {showBannerDialog === sponsor.sponsor_id && (
                <div className="mt-3 p-4 border rounded-lg bg-white space-y-3" data-testid={`banner-form-${sponsor.sponsor_id}`}>
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-bold">New Banner</h4>
                    <button onClick={() => setShowBannerDialog(null)} className="text-slate-400 hover:text-slate-600"><XIcon className="h-4 w-4" /></button>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Type</Label>
                      <Select value={bannerForm.banner_type} onValueChange={v => setBannerForm(f => ({ ...f, banner_type: v }))}>
                        <SelectTrigger data-testid="banner-type-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="top">Top Banner (1456x135)</SelectItem>
                          <SelectItem value="side">Side Banner (600x375)</SelectItem>
                          <SelectItem value="feed">In-Feed Banner (728x180) — appears every 20 forum posts</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Placement</Label>
                      <Select value={bannerForm.placement} onValueChange={v => setBannerForm(f => ({ ...f, placement: v }))}>
                        <SelectTrigger data-testid="banner-placement-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="app">Site-wide (App)</SelectItem>
                          <SelectItem value="forum">Forum</SelectItem>
                          {courses.map(c => <SelectItem key={c.course_id} value={`course:${c.course_id}`}>Course: {c.title}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Click URL</Label>
                    <Input data-testid="banner-click-url" value={bannerForm.click_url} onChange={e => setBannerForm(f => ({ ...f, click_url: e.target.value }))} placeholder="https://sponsor-website.com" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs">Campaign Start</Label><Input data-testid="banner-start-date" type="date" value={bannerForm.campaign_start} onChange={e => setBannerForm(f => ({ ...f, campaign_start: e.target.value }))} /></div>
                    <div><Label className="text-xs">Campaign End</Label><Input data-testid="banner-end-date" type="date" value={bannerForm.campaign_end} onChange={e => setBannerForm(f => ({ ...f, campaign_end: e.target.value }))} /></div>
                  </div>
                  <div>
                    <Label className="text-xs">Banner Image ({bannerForm.banner_type === 'top' ? '1456x135px' : bannerForm.banner_type === 'feed' ? '728x180px' : '600x375px'})</Label>
                    <Input data-testid="banner-file-input" type="file" accept="image/*" onChange={e => setBannerForm(f => ({ ...f, file: e.target.files[0] }))} />
                  </div>
                  <Button data-testid="save-banner-btn" onClick={() => handleCreateBanner(sponsor.sponsor_id)} className="w-full bg-gradient-to-r from-brand-navy to-brand-sage text-white">Upload Banner</Button>
                </div>
              )}
            </div>
          </Card>
        ))}
      </div>
      <ConfirmDialog {...dialogProps} />
    </div>
  );
}
